/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { ReactNode, SyntheticEvent } from "react";

import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Stack from "@mui/material/Stack";
import { SwitchProps } from "@mui/material/Switch";

import OptionalTooltip from "../Tooltip/OptionalTooltip";
import { CustomSwitch } from "./components/CustomSwitch";
import {
  generateEndItemMargin,
  generateStartItemMargin,
  startEnditemsAlignment,
  toggleSwitchLabelStyles
} from "./styleFunctions";
import { TOGGLE_SWITCH_LABEL_PLACEMENT, TOGGLE_SWITCH_SIZES } from "./types";

interface ToggleSwitchProps {
  size: TOGGLE_SWITCH_SIZES;
  preventParentOnClick?: boolean;
  labelPlacement?: TOGGLE_SWITCH_LABEL_PLACEMENT;
  label?: string;
  justifyContent?: string;
  startItem?: ReactNode;
  endItem?: ReactNode;
  testId?: string;
  tooltipText?: string;
  customCSS?: CSSObject;
}

const ToggleSwitch = ({
  size,
  preventParentOnClick,
  labelPlacement,
  label,
  justifyContent,
  startItem,
  endItem,
  testId,
  tooltipText = "",
  customCSS,
  ...props
}: ToggleSwitchProps & Omit<SwitchProps, "size">) => {
  const handleClick = (e: SyntheticEvent) => {
    if (preventParentOnClick) {
      e.stopPropagation();
    }
  };

  const switchComponent = (
    <CustomSwitch
      data-testid="custom-switch"
      customSize={size}
      labelPlacement={labelPlacement}
      checked={props.checked}
      {...props}
    />
  );

  return (
    <OptionalTooltip
      isDisabled={!tooltipText}
      tooltipTitle={tooltipText}
      tooltipPlacement="top-end"
      defaultWordBreak
    >
      <div
        data-testid={testId ?? "toggle-switch-component"}
        css={css(
          {
            display: "flex",
            justifyContent: justifyContent ?? "center"
          },
          customCSS
        )}
      >
        <Stack direction="row" alignItems="center" margin="0" spacing="0">
          {startItem && (
            <div
              css={css(startEnditemsAlignment, {
                marginRight: generateStartItemMargin(label, labelPlacement)
              })}
            >
              {startItem}
            </div>
          )}

          <FormGroup>
            <FormControlLabel
              disabled={props.disabled}
              control={
                preventParentOnClick ? (
                  <div onClick={handleClick}>{switchComponent}</div>
                ) : (
                  switchComponent
                )
              }
              label={label}
              labelPlacement={labelPlacement}
              sx={toggleSwitchLabelStyles(
                labelPlacement,
                endItem,
                startItem,
                label
              )}
            />
          </FormGroup>

          {endItem && (
            <div
              css={css(startEnditemsAlignment, {
                marginLeft: generateEndItemMargin(label, labelPlacement)
              })}
            >
              {endItem}
            </div>
          )}
        </Stack>
      </div>
    </OptionalTooltip>
  );
};

export default ToggleSwitch;
